import { supabase } from './supabase';

export interface AddonsLibraryItem {
  id: string;
  business_id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes: number;
  created_at: string;
  updated_at: string;
}

export interface CreateAddonsLibraryItemParams {
  business_id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes: number;
}

export interface UpdateAddonsLibraryItemParams {
  name?: string;
  description?: string;
  price?: number;
  duration_minutes?: number;
}

// Get all add-ons library items for a business
export async function getAddonsLibraryItems(businessId: string): Promise<AddonsLibraryItem[]> {
  const { data, error } = await supabase
    .from('addons_library')
    .select('*')
    .eq('business_id', businessId)
    .order('name');

  if (error) {
    console.error('Error fetching addons library items:', error);
    throw new Error(`Failed to fetch addons library items: ${error.message}`);
  }

  return data || [];
}

// Create a new add-ons library item
export async function createAddonsLibraryItem(params: CreateAddonsLibraryItemParams): Promise<AddonsLibraryItem> {
  const { data, error } = await supabase
    .from('addons_library')
    .insert([params])
    .select()
    .single();

  if (error) {
    console.error('Error creating addons library item:', error);
    throw new Error(`Failed to create addons library item: ${error.message}`);
  }

  return data;
}

// Update an add-ons library item
export async function updateAddonsLibraryItem(id: string, params: UpdateAddonsLibraryItemParams): Promise<AddonsLibraryItem> {
  const { data, error } = await supabase
    .from('addons_library')
    .update(params)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating addons library item:', error);
    throw new Error(`Failed to update addons library item: ${error.message}`);
  }

  return data;
}

// Delete an add-ons library item
export async function deleteAddonsLibraryItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('addons_library')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting addons library item:', error);
    throw new Error(`Failed to delete addons library item: ${error.message}`);
  }
}

// Get a single add-ons library item
export async function getAddonsLibraryItem(id: string): Promise<AddonsLibraryItem | null> {
  const { data, error } = await supabase
    .from('addons_library')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Item not found
    }
    console.error('Error fetching addons library item:', error);
    throw new Error(`Failed to fetch addons library item: ${error.message}`);
  }

  return data;
}
